<template>
  <div
    class="relative w-full max-w-md px-10 md:px-8 py-3 pb-6 text-dark-type-4"
  >
    <template v-if="isIframeApp()">
      <div class="mx-auto text-2xl font-bold text-center text-dark-type-3">
        Log in to your QuickTeller account to continue
      </div>
    </template>
    <template v-else>
      <div>
        <div
          class="cursor-pointer"
          @click="$emit('close')"
          v-if="showCloseToggle"
        >
          <svg
            class="absolute"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
          >
            <g fill="none" fill-rule="evenodd">
              <circle cx="12" cy="12" r="12" fill="#FCFCFC" />
              <path
                d="M16 8l-8 8m0-8l8 8"
                stroke="#131615"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              />
            </g>
          </svg>
        </div>
        <div class="mx-auto font-bold text-center text-dark-type-3 text-xl">
          {{
            showPasswordView
              ? loginType === "password"
                ? "Enter password"
                : "Confirm OTP"
              : "Log in to your account"
          }}
        </div>
      </div>

      <div
        class="flex items-start justify-center p-3 my-5 bg-red-200 rounded-md"
        v-if="errorMessage"
      >
        <div class="text-sm font-medium text-red-500">{{ errorMessage }}</div>
      </div>
      <div v-if="!showPasswordView">
        <div class="mt-2">
          <div class="text-xs font-medium text-center">
            Need a Shuttlers account?
            <router-link to="/register" class="text-active"
              >Register</router-link
            >
          </div>
        </div>

        <div class="mt-8">
          <div>
            <div class="container mx-auto">
              <ul class="flex justify-center space-x-2 text-white">
                <li>
                  <button
                    @click="currentTab(1)"
                    class="
                      inline-block
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-gray-500
                    "
                    :class="{
                      'bg-green-50 text-green-500 rounded-lg': tab === 1,
                    }"
                  >
                    Email address
                  </button>
                </li>
                <li>
                  <button
                    @click="currentTab(2)"
                    class="
                      inline-block
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-gray-500
                    "
                    :class="{
                      'bg-green-50 text-green-500 rounded-lg': tab === 2,
                    }"
                  >
                    Phone number
                  </button>
                </li>
              </ul>
              <div class="p-3">
                <div v-if="tab === 1">
                  <form @submit.prevent="proceedToPassword" class="mt-8">
                    <div>
                      <label class="text-xs font-medium" for="email"
                        >Email address</label
                      >
                      <div class="mt-2">
                        <input
                          v-model="$v.form.email.$model"
                          type="email"
                          id="email"
                          class="
                            w-full
                            h-12
                            px-3
                            py-4
                            text-xs
                            font-medium
                            outline-none
                            placeholder-label-type-1
                            focus:outline-none
                            rounded-lg
                            border border-solid border-gray-type-9
                          "
                          placeholder="sample@mail.com"
                        />
                        <span
                          class="text-xs text-red-400"
                          v-if="$v.form.email.$error"
                          >Please enter your email address</span
                        >
                      </div>
                    </div>
                    <button
                      type="submit"
                      class="
                        block
                        w-full
                        h-12
                        mt-10
                        py-4
                        font-bold
                        text-center text-white
                        rounded-lg
                      "
                      :class="
                        processing || !form.email
                          ? 'bg-gray-type-7'
                          : 'bg-dark-type-1'
                      "
                      :disabled="processing || !form.email"
                    >
                      {{ "Proceed" }}
                    </button>
                  </form>
                </div>
                <div v-if="tab === 2">
                  <form @submit.prevent="proceedToPassword" class="mt-8">
                    <div>
                      <label class="text-xs font-medium" for="phone"
                        >Phone number</label
                      >
                      <div class="mt-2 flex flex-row">
                        <select
                          class="
                            h-12
                            px-3
                            py-4
                            text-xs
                            font-medium
                            outline-none
                            placeholder-label-type-1
                            focus:outline-none
                            rounded-l-lg
                            border border-solid border-gray-type-9
                          "
                          v-model="form.country"
                        >
                          <option
                            :key="country.code"
                            v-for="country in countries"
                            v-bind:value="country"
                          >
                            {{ countryCodeToEmoji(country.code) }}
                            {{ country.phone_code }}
                          </option>
                        </select>

                        <input
                          v-model="form.phone"
                          type="number"
                          id="phone"
                          class="
                            w-full
                            h-12
                            px-3
                            py-4
                            text-xs
                            font-medium
                            outline-none
                            placeholder-label-type-1
                            focus:outline-none
                            rounded-r-lg
                            border border-solid border-gray-type-9
                          "
                          placeholder=""
                        />
                      </div>
                      <div
                        class="text-xs text-red-400 mt-2"
                        v-if="form.phone && !isPhoneValid"
                      >
                        Your phone number is not valid
                      </div>
                    </div>
                    <button
                      type="submit"
                      class="
                        block
                        w-full
                        h-12
                        mt-10
                        py-4
                        font-bold
                        text-center text-white
                        rounded-lg
                      "
                      :class="
                        processing || !form.phone || !isPhoneValid
                          ? 'bg-gray-type-7'
                          : 'bg-dark-type-1'
                      "
                      :disabled="processing || !form.phone || !isPhoneValid"
                    >
                      {{ "Proceed" }}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Password/OTP View -->
      <div v-if="showPasswordView">
        <!-- Login Type === Password -->
        <div v-if="loginType === 'password'">
          <p class="mt-1 text-sm text-center">
            You are about to login with
            <b>{{ tab === 1 ? form.email : form.phone }}</b>
          </p>
          <form @submit.prevent="login" class="mt-12">
            <div class="mt-6">
              <div class="flex justify-between">
                <span class="text-xs font-medium" for="password">Password</span>
                <span
                  class="text-xs font-medium text-active cursor-pointer"
                  @click="setLoginType('otp')"
                  >Login with OTP</span
                >
              </div>
              <div class="relative mt-2">
                <input
                  v-model="$v.form.password.$model"
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  class="
                    w-full
                    h-12
                    px-3
                    py-4
                    text-xs
                    font-medium
                    outline-none
                    placeholder-label-type-1
                    focus:outline-none
                    rounded-lg
                    border border-solid border-gray-type-9
                  "
                />
                <div
                  class="
                    absolute
                    inset-y-0
                    right-0
                    flex
                    items-center
                    pr-2
                    text-xs
                    font-medium
                    cursor-pointer
                    text-dark-type-2
                  "
                  @click="showPassword = !showPassword"
                >
                  {{ showPassword ? "Hide" : "Show" }}
                </div>
                <span
                  class="text-xs text-red-400"
                  v-if="!$v.form.password.required && $v.form.password.$error"
                  >Please enter a password</span
                >
              </div>
            </div>
            <button
              type="submit"
              class="
                block
                w-full
                h-12
                py-4
                mt-10
                font-bold
                text-center text-white
                rounded-lg
              "
              :class="processing ? 'bg-gray-type-7' : ' bg-dark-type-1'"
              :disabled="processing"
            >
              {{ processing ? "Processing..." : "Proceed with Login" }}
            </button>
            <div class="mt-10 text-center">
              <router-link
                to="/forgot-password"
                class="mt-2 text-xs font-medium text-dark-type-1"
                >Forgot Password?</router-link
              >
            </div>
          </form>
        </div>

        <!-- Login Type === OTP -->
        <div v-if="loginType === 'otp'">
          <p class="mt-1 text-sm text-center">
            Please enter the 4-digit code sent to
          </p>
          <p class="mt-1 text-sm text-center font-bold">
            {{ tab === 1 ? form.email : form.phone }}
          </p>
          <form @submit.prevent="loginWithOtp" class="mt-12">
            <div class="mt-6">
              <div class="flex justify-end">
                <!-- <span class="text-xs font-medium" for="password">Password</span> -->
                <span
                  class="text-xs font-medium text-active cursor-pointer"
                  @click="setLoginType('password')"
                  >Login with password</span
                >
              </div>
              <div class="flex justify-center mt-3">
                <v-otp-input
                  ref="otpInput"
                  input-classes="otp-input w-16 h-16 md:w-20 md:h-20 mx-2 rounded bg-gray-type-4 focus:outline-none focus-visible:border-gray-600 text-center "
                  separator=""
                  :num-inputs="4"
                  :should-auto-focus="true"
                  :is-input-num="true"
                  @on-change="handleOnChange"
                  @on-complete="handleOnComplete"
                />
              </div>

              <div class="text-center mt-6 text-xs">
                <p class="font-normal">Didn't receive code?</p>
                <div class="mt-1">
                  <countdown-timer
                    value="120"
                    @on-timer-restart="initiateOtp"
                  />
                </div>
              </div>
            </div>
            <button
              type="submit"
              class="
                block
                w-full
                h-12
                py-4
                mt-10
                font-bold
                text-center text-white
                rounded-lg
              "
              :class="
                !enableOtpLoginButton || processing
                  ? 'bg-gray-type-7'
                  : ' bg-dark-type-1'
              "
              :disabled="!enableOtpLoginButton || processing"
            >
              {{ processing ? "Processing..." : "Proceed with Login" }}
            </button>
          </form>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { isIframeApp } from "@/services/utils";
import { required, email } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import axios from "@/services/axios";
import CountdownTimer from "@/components/CountdownTimer";
import { isValidPhoneNumber } from "libphonenumber-js/mobile";
import { countryCodeEmoji } from "country-code-emoji";

export default {
  components: { CountdownTimer },
  props: {
    showCloseToggle: {
      type: Boolean,
      default: true,
    },
    goBack: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPassword: false,
      errorMessage: "",
      form: {
        email: "",
        password: "",
        phone: "",
        country: null,
      },
      processing: false,
      tab: 1,
      showPasswordView: false,
      loginType: "password",
      otpValue: "",
      otpReferenceId: "",
      enableOtpLoginButton: false,
      countries: [],
    };
  },
  validations: {
    form: {
      email: {
        email,
      },
      password: {
        required,
      },
    },
  },
  created() {
    this.fetchCountries();
  },
  computed: {
    ...mapGetters("auth", ["user"]),
  },
  watch: {
    goBack(value) {
      this.showPasswordView = value;
    },
    "form.phone"() {
      this.validatePhoneNumber();
    },
    loginType() {
      this.errorMessage = "";
    },
    showPasswordView() {
      this.errorMessage = "";
    },
  },
  methods: {
    countryCodeToEmoji(code) {
      return countryCodeEmoji(code);
    },
    async fetchCountries() {
      const response = await axios.get(`/countries`);
      this.countries = response.data;
      this.form.country = this.countries[0];
    },
    validatePhoneNumber() {
      this.isPhoneValid = isValidPhoneNumber(
        this.form.phone,
        this.form.country.code
      );
    },
    currentTab: function (tabNumber) {
      this.tab = tabNumber;
    },
    proceedToPassword() {
      this.$emit("show-back-button");
    },
    setLoginType(loginType) {
      this.loginType = loginType;

      if (loginType === "otp") {
        this.initiateOtp();
      }
    },

    handleOnComplete(value) {
      this.otpValue = value;
      this.enableOtpLoginButton = true;
    },
    handleOnChange() {
      this.enableOtpLoginButton = false;
    },
    async initiateOtp() {
      let payload = null;
      if (this.tab === 1) {
        payload = {
          email: this.form.email,
          type: "user",
        };
      } else {
        payload = {
          phone: this.form.phone,
          country_code: this.form.country.code,
          type: "user",
        };
      }
      const response = await axios.post(`/otp-login`, payload);
      this.otpReferenceId = response.data.reference_id;
    },
    loginWithOtp() {
      this.processing = true;
      this.errorMessage = null;

      const payload = {
        otpCode: this.otpValue,
        type: "user",
        referenceId: this.otpReferenceId,
      };

      this.$store
        .dispatch("auth/otpLogin", payload)
        .then(async () => {
          const redirect = this.$route.query.redirect;
          if (redirect) {
            this.$router.push({
              path: redirect,
              query: { ...this.$route.query },
            });
          } else {
            this.$router.push({
              name: "dashboard.home",
              query: { ...this.$route.query },
            });
          }
        })
        .catch((err) => {
          if (err.response && err.response.data.message) {
            this.errorMessage = err.response.data.message;
          } else {
            this.errorMessage = "Oops! An error occurred. Please try again.";
          }
        })
        .finally(() => (this.processing = false));
    },
    login() {
      this.$v.form.$touch();

      if (this.processing || this.$v.form.$error) {
        return;
      }

      this.processing = true;
      this.errorMessage = null;

      let payload = null;
      if (this.tab === 1) {
        payload = {
          email: this.form.email,
          password: this.form.password,
        };
      } else {
        if (this.tab === 2) {
          payload = {
            phone: this.form.phone,
            password: this.form.password,
            country_code: this.form.country.code,
          };
        }
      }

      this.$store
        .dispatch("auth/login", payload)
        .then(async () => {
          const redirect = this.$route.query.redirect;
          if (redirect) {
            this.$router.push({
              path: redirect,
              query: { ...this.$route.query },
            });
          } else {
            this.$router.push({
              name: "dashboard.home",
              query: { ...this.$route.query },
            });
          }
        })
        .catch((err) => {
          if (err.response && err.response.data.message) {
            this.errorMessage = err.response.data.message;
          } else {
            this.errorMessage = "Oops! An error occurred. Please try again.";
          }
        })
        .finally(() => (this.processing = false));
    },
    isIframeApp() {
      return isIframeApp();
    },
  },
};
</script>

<style>
#select-country {
  width: 100px;
}

#select-country .vs__search::placeholder,
#select-country .vs__dropdown-toggle,
#select-country.vs__dropdown-menu {
  height: 48px;
  border-color: rgba(229, 233, 242, 1);
  border-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
#select-country .vs__selected-options .vs__search {
  display: none;
}
#select-country .vs--open {
  width: 100%;
}
#select-country .vs__dropdown-option--highlight {
  background-color: rgba(0, 224, 105);
}
</style>

<style scoped lang="scss">
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.otp-input {
  width: 40px;
  height: 40px;
  padding: 5px;
  margin: 0 10px;
  font-size: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  text-align: "center";
}
.error {
  border: 1px solid red !important;
}
.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>